import React, { useState, useCallback } from "react";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useStoreState } from 'easy-peasy';

import { Row, Col, Card, CardBody } from "reactstrap";
import { Typography, Button, Link, Modal, IconButton, Spinner, Popover } from "stories/components";
import { roundScaleDecimals } from "services/helpers"

import PreTransactionEdit from "views/app/PreTransactionEdit/PreTransactionEdit"
import PreOptionEdit from "views/app/PreOptionEdit/PreOptionEdit"
import FileDownloadButton from "components/Buttons/FileDownloadButton/FileDownloadButton"
import { httpReprocessFile } from "services/files"
import { attachTerminationToPrimary, groupPartialTerminationPreOptions } from "views/app/PreOptionEdit/groupPreOptions"
import { useAppDialog } from "components/AppDialog/useAppDialog"
import { useTargetPortfolio } from "components/TargetPortfolioDialog/useTargetPortfolio"

const EmailFilesList = ({ email, onRefresh }) => {
  
  const { t } = useTranslation();
  const { dialog, notify } = useAppDialog();
  const account = useStoreState((state) => state.account?.account);
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  // A pre-record is processed in its own portfolio (#1409)
  const { forRow } = useTargetPortfolio();

  // Modal: PreTransaction Edit
  const [modalPreTransactionOpen, setModalPreTransactionOpen] = useState(false);
  const [modalPreTransactionBatch, setModalPreTransactionBatch] = useState(null);

  // Modal: PreOption Edit
  const [modalPreOptionOpen, setModalPreOptionOpen] = useState(false);
  const [modalPreOptionBatch, setModalPreOptionBatch] = useState(null);
  
  // Reprocessing state
  const [reprocessingFiles, setReprocessingFiles] = useState({});

  const toggleModalPreTransaction = () => {
    setModalPreTransactionOpen(s => !s);
  }
  
  const openModalPreTransaction = (batch, file) => {
    // Add file reference to batch object for PreTransactionEdit
    const batchWithFile = {
      ...batch,
      file: file
    };
    forRow(batch?.portfolio);
    setModalPreTransactionOpen(true);
    setModalPreTransactionBatch(batchWithFile);
  }
  
  const modalPreTransactionResolve = () => {
    setModalPreTransactionOpen(false);
    setModalPreTransactionBatch(null);
    if (onRefresh) {
      onRefresh();
    }
  }
  
  const modalPreTransactionReject = () => {
    setModalPreTransactionOpen(false);
    setModalPreTransactionBatch(null);
  }

  // PreOption Modal functions
  const toggleModalPreOption = () => {
    setModalPreOptionOpen(s => !s);
  }
  
  const openModalPreOption = (batch, file) => {
    // Add file reference to batch object for PreOptionEdit
    const batchWithFile = {
      ...batch,
      file: file
    };
    forRow(batch?.portfolio);
    setModalPreOptionOpen(true);
    setModalPreOptionBatch(batchWithFile);
  }
  
  const modalPreOptionResolve = () => {
    setModalPreOptionOpen(false);
    setModalPreOptionBatch(null);
    if (onRefresh) {
      onRefresh();
    }
  }
  
  const modalPreOptionReject = () => {
    setModalPreOptionOpen(false);
    setModalPreOptionBatch(null);
  }
  
  const handleReprocessFile = (fileId) => {
    setReprocessingFiles(prev => ({ ...prev, [fileId]: true }));
    
    httpReprocessFile(fileId).then(res => {
      console.log("Reprocess success:", res);
      setReprocessingFiles(prev => ({ ...prev, [fileId]: false }));
      if (onRefresh) {
        onRefresh();
      }
    }, error => {
      console.error("Reprocess error:", error);
      setReprocessingFiles(prev => ({ ...prev, [fileId]: false }));
      notify(t('Uudelleenprosessointi epäonnistui', 'Reprocessing failed'));
    });
  }

  const renderPreTransactionRow = (preTransaction, file, fileIndex, transIndex) => {
    return (
      <div key={`pretrans-${fileIndex}-${transIndex}`} className="border-left border-info pl-3 py-3 ml-3 mb-2 bg-white rounded shadow-sm">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-grow-1" style={{gap: '2rem'}}>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Päivä')}</small>
              <strong>{preTransaction.date_transaction ? moment(preTransaction.date_transaction).format('DD.MM.YYYY') : '-'}</strong>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Arvopaperi')}</small>
              <strong className="text-primary">{preTransaction.import_name || preTransaction.import_ticker_symbol || '-'}</strong>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Määrä')}</small>
              <span>{preTransaction.quantity || '-'}</span>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Hinta')}</small>
              <span>{preTransaction.price ? roundScaleDecimals(preTransaction.price, 2) : '-'}</span>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Välittäjä')}</small>
              <span>{preTransaction.broker_name || '-'}</span>
            </div>
          </div>
          <div className="ml-3">
            <Button 
              name="process_pretransaction_button" 
              className="btn-sm btn-action" 
              color="secondary" 
              onClick={() => openModalPreTransaction(preTransaction, file)}
            >
              {t("Käsittele")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderPreOptionRow = (preOption, file, fileIndex, optIndex, isPartialPair = false) => {
    // Find portfolio name from portfolios list using portfolio id
    const portfolioName = portfolios?.find(p => p.id === preOption.portfolio)?.name || '-';
    
    // Map derivative_action to translated text
    const getDerivativeActionText = (action, isPartialPair) => {
      if (isPartialPair) {
        return t('derivative_action_partial_event', 'Osittainen terminointi');
      }
      const actionMap = {
        'create': t('derivative_action_create', 'Create'),
        'partial_terminate': t('derivative_action_partial_terminate', 'Partial terminate'),
        'full_terminate': t('derivative_action_full_terminate', 'Full terminate'),
      };
      return actionMap[action] || '-';
    };
    
    return (
      <div key={`preopt-${fileIndex}-${optIndex}`} className="border-left border-warning pl-3 py-3 ml-3 mb-2 bg-white rounded shadow-sm">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-grow-1" style={{gap: '2rem'}}>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Salkku')}</small>
              <strong>{portfolioName}</strong>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Kohde-etuus')}</small>
              <strong className="text-primary">{preOption.asset_name || '-'}</strong>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Tyyppi')}</small>
              <span>{preOption.derivative_type === 2 ? t("Option") : t("Forward")}</span>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('derivative_action_label', 'Toiminto')}</small>
              <span>{getDerivativeActionText(preOption.derivative_action, isPartialPair)}</span>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Välittäjä')}</small>
              <span>{preOption.broker_name || '-'}</span>
            </div>
            <div className="flex-fill">
              <small className="text-muted d-block">{t('Päättymispäivä')}</small>
              <span>{preOption.end_date ? moment(preOption.end_date).format('DD.MM.YYYY') : '-'}</span>
            </div>
          </div>
          <div className="ml-3">
            <Button 
              name="process_preoption_button" 
              className="btn-sm btn-action" 
              color="secondary" 
              onClick={() => openModalPreOption(preOption, file)}
            >
              {t("Käsittele")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderFile = (file, index) => {
    // Filtter unprocessed and processed pre-transactions
    const unprocessedPreTransactions = file.pre_transactions_data?.filter(pt => !pt.processed) || [];
    const processedPreTransactions = file.pre_transactions_data?.filter(pt => pt.processed) || [];
    
    // Review status distinguishes accepted records from skipped records.
    const unprocessedPreOptions = file.pre_options_data?.filter(po =>
      po.review_status ? po.review_status === 'pending' : !po.processed
    ) || [];
    const processedPreOptions = file.pre_options_data?.filter(po =>
      po.review_status ? po.review_status === 'accepted' : po.processed
    ) || [];
    const unprocessedPreOptionGroups = groupPartialTerminationPreOptions(unprocessedPreOptions);
    const processedPreOptionGroups = groupPartialTerminationPreOptions(processedPreOptions);
    
    // Get created transactions from file level
    const createdTransactions = file.transactions_data || [];
    
    const hasUnprocessedPreTransactions = unprocessedPreTransactions.length > 0;
    const hasProcessedPreTransactions = processedPreTransactions.length > 0;
    const hasCreatedTransactions = createdTransactions.length > 0;
    const hasUnprocessedPreOptions = unprocessedPreOptions.length > 0;
    const hasProcessedPreOptions = processedPreOptions.length > 0;
    const hasProcessingErrors = file.processing_errors && file.processing_errors.length > 0;
    const alreadyProcessedNotice = file.processing_notice?.type === 'already_processed'
      ? file.processing_notice
      : null;

    return (
      <Card key={`file-${file.id}`} className="mb-3">
        <CardBody>
          {/* File Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-file fa-2x text-primary mr-3"></i>
              <div>
                <Typography variant="h4" className="mb-0">{file.filename}</Typography>
                <small className="text-muted">{file.description || ''}</small>
              </div>
            </div>
            <div className="d-flex align-items-center">
              {hasProcessingErrors && (
                <span className="badge badge-danger mr-2">
                  {file.processing_errors.length} {t('VIRHETTÄ')}
                </span>
              )}
              {alreadyProcessedNotice && (
                <span className="badge badge-info mr-2">
                  {t('file_already_processed_badge', 'Käsitelty aiemmin')}
                </span>
              )}
              {file.ai_processed_at && (
                <span className="badge badge-info mr-2">
                  <i className="fas fa-check mr-1"></i>
                  {t('Käsitelty')} {moment(file.ai_processed_at).format('DD.MM.YYYY')}
                </span>
              )}
              <FileDownloadButton 
                showPreview={true} 
                fileId={file.id} 
                fileName={file.filename} 
              />
              {/* Reprocess button - icon only, same style as Lataa/Esikatsele (no background) */}
              {(processedPreTransactions.length === 0 && processedPreOptions.length === 0 && createdTransactions.length === 0 && !alreadyProcessedNotice) && (
                <>
                  <Button
                    id={`reprocessFile-${file.id}`}
                    className="text-white font-weight-light rounded-circle custom-xs-button-input b-0 ml-2"
                    style={{ position: 'relative', zIndex: 0 }}
                    variant="lighter"
                    onClick={() => handleReprocessFile(file.id)}
                    disabled={reprocessingFiles[file.id]}
                  >
                    {reprocessingFiles[file.id] ? (
                      <i className="fas fa-spinner fa-spin text-primary" />
                    ) : (
                      <i className="fas fa-sync-alt text-primary" />
                    )}
                  </Button>
                  <Popover placement="left" target={`reprocessFile-${file.id}`}>
                    <Typography className="text-dark mb-1" variant="small">
                      {t('Uudelleen prosessoi', 'Uudelleen prosessoi')}
                    </Typography>
                  </Popover>
                </>
              )}
            </div>
          </div>

          {/* Unprocessed PreTransactions */}
          {hasUnprocessedPreTransactions && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-exchange-alt text-primary mr-2"></i>
                <Typography variant="h5" className="mb-0">
                  {t('Käsiteltävät transaktiot')} ({unprocessedPreTransactions.length})
                </Typography>
              </div>
              {unprocessedPreTransactions.map((preTransaction, transIndex) => 
                renderPreTransactionRow(preTransaction, file, index, transIndex)
              )}
            </div>
          )}

          {/* Created Transactions */}
          {hasCreatedTransactions && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-check-circle text-success mr-2"></i>
                <Typography variant="h5" className="mb-0">
                  {t('Luodut transaktiot')} ({createdTransactions.length})
                </Typography>
              </div>
              {createdTransactions.map((transaction, tIndex) => (
                <div key={`created-trans-${tIndex}`} className="border-left border-success pl-3 py-3 ml-3 mb-2 bg-white rounded shadow-sm">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex flex-grow-1" style={{gap: '2rem'}}>
                      <div className="flex-shrink-0">
                        <i className="fas fa-check-circle text-success fa-2x"></i>
                      </div>
                      <div className="flex-fill">
                        <small className="text-muted d-block">{t('Transaction ID')}</small>
                        <strong className="text-success">{transaction.id}</strong>
                      </div>
                      <div className="flex-fill">
                        <small className="text-muted d-block">{t('Salkku')}</small>
                        <span>{transaction.portfolio_name || transaction.portfolio || '-'}</span>
                      </div>
                      <div className="flex-fill">
                        <small className="text-muted d-block">{t('Ticker')}</small>
                        <strong className="text-primary">{transaction.ticker_symbol || '-'}</strong>
                      </div>
                      <div className="flex-fill">
                        <small className="text-muted d-block">{t('Päivä')}</small>
                        <span>{transaction.date_transaction ? moment(transaction.date_transaction).format('DD.MM.YYYY') : '-'}</span>
                      </div>
                      {/* <div className="flex-fill">
                        <small className="text-muted d-block">{t('Tyyppi')}</small>
                        <span>{transaction.type || '-'}</span>
                      </div> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Unprocessed PreOptions */}
          {hasUnprocessedPreOptions && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-balance-scale-left text-warning mr-2"></i>
                <Typography variant="h5" className="mb-0">
                  {t('Käsiteltävät johdannaiset')} ({unprocessedPreOptionGroups.length})
                </Typography>
              </div>
              {unprocessedPreOptionGroups.map((group, optIndex) => 
                renderPreOptionRow(attachTerminationToPrimary(group), file, index, optIndex, group.isPartialPair)
              )}
            </div>
          )}

          {/* Processed PreOptions Count */}
          {hasProcessedPreOptions && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-check-circle text-success mr-2"></i>
                <Typography variant="h5" className="mb-0">
                  {t('Prosessoidut johdannaiset')}
                </Typography>
              </div>
              <div className="alert alert-success mb-0">
                <i className="fas fa-info-circle mr-2"></i>
                {processedPreOptionGroups.length} {t('johdannaista prosessoitu onnistuneesti')}
              </div>
            </div>
          )}

          {alreadyProcessedNotice && (
            <div className="alert alert-info mt-3 mb-0">
              <i className="fas fa-info-circle mr-2"></i>
              {t('file_already_processed_message', 'Käsitelty jo aiemmin')}
              {alreadyProcessedNotice.processed_at
                ? ` ${moment(alreadyProcessedNotice.processed_at).format('DD.MM.YYYY HH:mm')}`
                : ''}
            </div>
          )}

          {/* Processing Errors */}
          {hasProcessingErrors && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-exclamation-circle text-danger mr-2"></i>
                <Typography variant="h5" className="mb-0 text-danger">
                  {t('Käsittelyvirheet')} ({file.processing_errors.length})
                </Typography>
              </div>
              {file.processing_errors.map((error, errIndex) => (
                <div key={`error-${errIndex}`} className="alert alert-danger mb-2">
                  <div><strong>{error.error_title || error.error_stage}</strong></div>
                  <div>{error.error_message}</div>
                </div>
              ))}
            </div>
          )}

          {/* No Unprocessed PreTransactions or PreOptions */}
          {!hasUnprocessedPreTransactions && !hasUnprocessedPreOptions && !hasProcessingErrors && (
            <div className="text-center text-muted py-3">
              <i className="fas fa-check-circle fa-2x mb-2"></i>
              <div>{t('Ei käsiteltäviä kirjauksia')}</div>
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  if (!email) {
    return dialog;
  }

  const hasFiles = email.files && email.files.length > 0;

  return (
    <>
      {dialog}
      <div className="mt-3 mb-3">
        {/* Files Section */}
        {hasFiles ? (
          <>
            <Typography variant="h4" className="mb-3">
              {t('Sähköpostin tiedostot')} ({email.files.length})
            </Typography>
            
            {email.files.map((file, index) => renderFile(file, index))}
          </>
        ) : (
          <div className="text-center text-muted py-3">
            <i className="fas fa-inbox fa-2x mb-2"></i>
            <div>{t('Ei tiedostoja')}</div>
          </div>
        )}

        {/* Email Body/Message - After files */}
        {email.body && (
          <Card className="mb-3 mt-3">
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-envelope-open-text fa-2x text-primary mr-3"></i>
                <Typography variant="h4" className="mb-0">{t('Sähköpostin viesti')}</Typography>
              </div>
              <div className="p-3 bg-light rounded" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                {email.body}
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* PreTransaction Edit Modal */}
      <Modal
        title={t("pretransaction_edit_title", "Käsittele kirjaus")}
        showHeader={false}
        isOpen={modalPreTransactionOpen}
        toggleModal={toggleModalPreTransaction}
        size="modal-xxl"
      >
        <PreTransactionEdit
          object={modalPreTransactionBatch}
          handleResolve={modalPreTransactionResolve}
          handleReject={modalPreTransactionReject}
        />
      </Modal>

      {/* PreOption Edit Modal */}
      <Modal
        title={t("preoption_edit_title", "Käsittele johdannainen")}
        showHeader={false}
        isOpen={modalPreOptionOpen}
        toggleModal={toggleModalPreOption}
        size="modal-xxl"
      >
        <PreOptionEdit
          object={modalPreOptionBatch}
          handleResolve={modalPreOptionResolve}
          handleReject={modalPreOptionReject}
        />
      </Modal>
    </>
  );
}

export default EmailFilesList;

